import React, { useState } from "react";
import { Space, Button, Typography, Badge, Tooltip } from "antd";
import { SolutionOutlined, UploadOutlined, RightOutlined } from "@ant-design/icons";
import { Medication, SCRIPT_STATUS_CONFIG } from "../../../types/healthcare/MedicationTypes";
import { SectionCard, StatusTag, InlineLoading, EmptyState } from "../../../components/shared";
import ScriptUploadModal from "./ScriptUploadModal";
import type { UserUuid } from "../../../types/identity";

const { Text } = Typography;

interface Props {
	medications: Medication[];
	loading: boolean;
	patientId: UserUuid;
	onRefresh: () => void;
}

const PatientMedicationsSection: React.FC<Props> = ({ medications, loading, patientId, onRefresh }) => {
	const [expanded, setExpanded] = useState(false);
	const [uploadTarget, setUploadTarget] = useState<Medication | null>(null);

	const active = medications.filter((m) => m.active);
	const pendingScripts = medications.filter((m) => m.script_status === "pending");
	const visible = expanded ? medications : medications.slice(0, 3);

	return (
		<>
			<SectionCard
				style={{ height: "100%" }}
				title={
					<Space size={8}>
						<SolutionOutlined style={{ fontSize: 18, color: "var(--mg-primary)" }} />
						My Medications
						{/* Inline count, matching every other section on the patient dashboard and the
						    sidebar badge that mirrors it. Hidden at zero, never rendered as "0" (D3).
						    It lives here rather than on the dashboard's wrapper because this component
						    owns the whole SectionCard — putting it there would render two titles.
						    Distinct from the Badge beside it: this is "how many medications", that is
						    "how many need a script". */}
						{medications.length > 0 && (
							<Text type="secondary" style={{ fontWeight: 400, fontVariantNumeric: "tabular-nums" }}>
								{medications.length}
							</Text>
						)}
						{pendingScripts.length > 0 && <Badge count={pendingScripts.length} title={`${pendingScripts.length} script(s) required`} />}
					</Space>
				}
			>
				{loading ? (
					<InlineLoading minHeight={200} />
				) : medications.length > 0 ? (
					<div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
						{visible.map((med, i) => {
							const scriptCfg = med.script_status ? SCRIPT_STATUS_CONFIG[med.script_status] : null;
							const needsScript = med.script_status === "pending";

							return (
								<div
									key={med.id}
									className="animate-card-appear"
									style={{
										animationDelay: `${i * 0.1}s`,
										border: needsScript ? "1px solid var(--mg-warning)" : "1px solid var(--mg-border)",
										background: needsScript ? "var(--mg-warning-bg, #fffbe6)" : undefined,
										borderRadius: 8,
										padding: "12px 16px",
									}}
								>
									<div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
										<div style={{ flex: 1, minWidth: 0 }}>
											<div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
												<Text strong style={{ fontSize: 14 }}>{med.medication_name}</Text>
											<StatusTag status={med.active ? "active" : "inactive"} />
											</div>

											<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 2 }}>
												{med.dosage}{med.frequency ? ` · ${med.frequency}` : ""}
											</Text>

											{scriptCfg && (
												<div style={{ marginTop: 4 }}>
											<StatusTag status={med.script_status === "verified" ? "completed" : med.script_status === "pending" ? "pending" : "inactive"} label={scriptCfg.label} />
													{med.script_notes && (
														<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 2 }}>
															{med.script_notes}
														</Text>
													)}
												</div>
											)}
										</div>

										{needsScript && (
											<Tooltip title="Upload your prescription script">
												<Button type="primary" size="small" icon={<UploadOutlined />} onClick={() => setUploadTarget(med)} style={{ flexShrink: 0 }}>
													Upload Script
												</Button>
											</Tooltip>
										)}
									</div>
								</div>
							);
						})}

						{medications.length > 3 && (
							<Button type="link" size="large" style={{ width: "100%", marginTop: 4 }} onClick={() => setExpanded((p) => !p)}>
								{expanded ? "Show less" : `View all ${medications.length} medications`}
								<RightOutlined style={{ fontSize: 12, transform: expanded ? "rotate(90deg)" : "none" }} />
							</Button>
						)}
					</div>
				) : (
					<EmptyState title="No medications assigned" icon={<SolutionOutlined style={{ fontSize: 48, color: "var(--mg-border)" }} />} />
				)}
			</SectionCard>

			<ScriptUploadModal
				medication={uploadTarget}
				visible={!!uploadTarget}
				patientId={patientId}
				onClose={() => setUploadTarget(null)}
				onSuccess={() => {
					setUploadTarget(null);
					onRefresh();
				}}
			/>
		</>
	);
};

export default PatientMedicationsSection;
